import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { 
  createChart, 
  IChartApi, 
  ISeriesApi, 
  CandlestickData, 
  LineData, 
  UTCTimestamp,
  LineSeries,
  AreaSeries,
  HistogramSeries,
  CandlestickSeries
} from 'lightweight-charts';
import { Eye, EyeOff, Play, Pause, Settings, X } from 'lucide-react';
import Loader from './Loader';
import { formatPriceWithAssetDecimals, getAssetDecimalPlaces } from '../utils/priceFormatter';
import { detectOrderType } from '../utils/orderTypeDetector';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { useTheme } from '../contexts/ThemeContext';
import { useAITrading } from '../contexts/AITradingContext';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import { useAccount } from '../contexts/AccountContext';
import { useAIStrategyPersistenceContext } from '../contexts/AIStrategyPersistenceContext';
import { usePriceInput } from '../contexts/PriceInputContext';
import { useOrderContext } from '../contexts/OrderContext';
import { usePositionContext } from '../contexts/PositionContext';
import { useChartPositionContext } from '../contexts/ChartPositionContext';
import { useTradingError } from '../contexts/TradingErrorContext';
import TradingErrorModal from './TradingErrorModal';
import ChartPositionManager from './ChartPositionManager';
import ChartingTools, { DrawingTool, DrawingElement } from './ChartingTools';
import AITradingSettings from './ai-trading/AITradingSettings';
import { useUnifiedAITrading } from '../hooks/useUnifiedAITrading';
import NumberInput from './NumberInput';

interface TradingChartProps {
  symbol: string;
  timeframe: string;
  chartType: string;
  onPriceUpdate?: (price: string, change: string, isPositive: boolean) => void;
  onKlinePriceUpdate?: (formattedPrice: string, closePrice: number, volume: number) => void;
  isRightPanelVisible?: boolean;
}

interface BinanceKline {
  [0]: number; // Open time
  [1]: string; // Open
  [2]: string; // High
  [3]: string; // Low
  [4]: string; // Close
  [5]: string; // Volume
  [6]: number; // Close time
  [7]: string; // Quote asset volume
  [8]: number; // Number of trades
  [9]: string; // Taker buy base asset volume
  [10]: string; // Taker buy quote asset volume
  [11]: string; // Ignore
}

const TradingChart: React.FC<TradingChartProps> = ({
  symbol,
  timeframe,
  chartType,
  onPriceUpdate,
  onKlinePriceUpdate,
}) => {
  // Volume bar configuration - set to false to disable volume bars
  const SHOW_VOLUME_BARS = false;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const [mainSeries, setMainSeries] = useState<ISeriesApi<"Candlestick"> | null>(null);
  const [isChartDisposed, setIsChartDisposed] = useState(false);
  const greenVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const redVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSymbolChanging, setIsSymbolChanging] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<string>('0.00');
  const [priceChange, setPriceChange] = useState<string>('+0.00%');
  const [isPositiveChange, setIsPositiveChange] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [chartData, setChartData] = useState<CandlestickData[]>([]);
  const [volumeData, setVolumeData] = useState<LineData[]>([]);
  const [greenVolumeData, setGreenVolumeData] = useState<LineData[]>([]);
  const [redVolumeData, setRedVolumeData] = useState<LineData[]>([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [hasUserPositionedChart, setHasUserPositionedChart] = useState(false);
  const [prevSymbol, setPrevSymbol] = useState(symbol);
  const [showPositionOverlay, setShowPositionOverlay] = useState(true);
  const [selectedDrawingTool, setSelectedDrawingTool] = useState<DrawingTool>('none');
  const [drawingElements, setDrawingElements] = useState<DrawingElement[]>([]);
  const [showAISettings, setShowAISettings] = useState(false);
  
  // Loading states for chart actions
  const [isBuyLoading, setIsBuyLoading] = useState(false);
  const [isSellLoading, setIsSellLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  
  // Use unified AI trading hook
  const {
    aiState,
    handlers,
    currentFirestoreStrategy: aiCurrentFirestoreStrategy,
    currentAssetConfig: aiCurrentAssetConfig,
    assetAIConfig,
    refreshTrigger
  } = useUnifiedAITrading(
    symbol,
    () => setShowAISettings(true),
    () => setShowAISettings(false)
  );
  
  // Debug logging for refreshTrigger
  // No logging needed
  const [isHolding, setIsHolding] = useState(false);
  const [showDateTime, setShowDateTime] = useState(false);
  const [buyOrderType, setBuyOrderType] = useState<'limit' | 'stop' | null>(null);
  const [sellOrderType, setSellOrderType] = useState<'limit' | 'stop' | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use price input context
  const { 
    buyPrice, 
    sellPrice, 
    showBuyInput, 
    showSellInput, 
    setBuyPrice, 
    setSellPrice, 
    fillBuyPrice, 
    fillSellPrice, 
    setBuyPriceInputVisible, 
    setSellPriceInputVisible 
  } = usePriceInput();
  const fetchInProgressRef = useRef<boolean>(false);

  // Initialize prevSymbol on mount to prevent false symbol change detection
  useEffect(() => {
    if (!prevSymbol && symbol) {
      setPrevSymbol(symbol);
    }
  }, [symbol, prevSymbol]);

  // Show date and time after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDateTime(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Update buy order type when buy price or current price changes
  useEffect(() => {
    if (showBuyInput && buyPrice && currentPrice) {
      const inputPrice = parseFloat(buyPrice);
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      if (!isNaN(inputPrice) && !isNaN(currentPriceNum)) {
        const orderTypeDetection = detectOrderType('buy', inputPrice, currentPriceNum);
        setBuyOrderType(orderTypeDetection.orderType);
      }
    } else {
      setBuyOrderType(null);
    }
  }, [showBuyInput, buyPrice, currentPrice]);

  // Update sell order type when sell price or current price changes
  useEffect(() => {
    if (showSellInput && sellPrice && currentPrice) {
      const inputPrice = parseFloat(sellPrice);
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      if (!isNaN(inputPrice) && !isNaN(currentPriceNum)) {
        const orderTypeDetection = detectOrderType('sell', inputPrice, currentPriceNum);
        setSellOrderType(orderTypeDetection.orderType);
      }
    } else {
      setSellOrderType(null);
    }
  }, [showSellInput, sellPrice, currentPrice]);

  // Use Binance market data hook for real-time updates
  const { getMarketDataForSymbol, isConnected: isBinanceConnected } = useBinanceMarketData();
  
  // Use theme context
  const { theme } = useTheme();

  // Use AI trading context for background trading (legacy for compatibility)
  const { 
    updatePriceForAI,
    getStrategy, 
    updateStrategy,
    updateSignal,
    updateConfig,
    startStrategy,
    stopStrategy,
    closeAllAIPositions
  } = useAITrading();

  // Use Backend AI trading context for persistent settings
  const {
    isAIEnabled,
    strategies,
    activeStrategies,
    enableAI,
    disableAI,
    addStrategy,
    activateStrategy,
    deactivateStrategy,
    // New per-asset methods
    getAssetAIConfig,
    isAssetAIEnabled,
    setAssetAIEnabled,
    stopAssetAITrading, // NEW: Comprehensive stop method
    saveAssetStrategy
  } = useBackendAITrading();
  
  // Import AI strategy persistence for saving Firestore updates
  const { saveStrategy } = useAIStrategyPersistenceContext();
  
  // Use order and position contexts for trading
  const { executeOrder } = useOrderContext();
  const { addOpenPosition } = usePositionContext();
  const { chartPositions, syncWithPendingOrders } = useChartPositionContext();
  const { error: tradingError, hideError, retryOrder, setOnRetry } = useTradingError();
  const { getAccountBalance } = useAccount();
  const { accountType } = useAccountType();
  const { user } = useAuth();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);

  // Handle Buy button click
  const handleBuyClick = useCallback(async () => {
    if (isBuyLoading) return; // Prevent multiple clicks
    
    setIsBuyLoading(true);
    try {
      let orderDetails;
      
      // Calculate trade amount as 20% of total balance
      const currentBalance = accountBalance.totalBalance;
      const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      const amount = Math.max(1.10, tradeAmountUSD / currentPriceNum); // Minimum 1.10 quantity
      
      // If input is visible, determine order type based on price comparison
      if (showBuyInput && buyPrice) {
        const inputPrice = parseFloat(buyPrice);
        const orderTypeDetection = detectOrderType('buy', inputPrice, currentPriceNum);
        
        orderDetails = {
          symbol: symbol,
          side: 'buy' as const,
          type: orderTypeDetection.orderType as 'limit' | 'stop',
          price: orderTypeDetection.isStopOrder ? currentPriceNum : inputPrice, // For stop orders, use current price as execution price
          stopPrice: orderTypeDetection.isStopOrder ? inputPrice : undefined, // For stop orders, use input price as stop price
          amount: amount,
          leverage: 100, // 100x leverage
        };
      } else {
        // Execute market order
        orderDetails = {
          symbol: symbol,
          side: 'buy' as const,
          type: 'market' as const,
          price: currentPriceNum,
          amount: amount,
          leverage: 100, // 100x leverage
        };
      }

      // Set up retry functionality
      setOnRetry(() => () => handleBuyClick());
      
      // Execute the order
      const result = await executeOrder(orderDetails);
      
      if (result.success) {
        // Reset input state after successful order
        if (showBuyInput) {
          setBuyPriceInputVisible(false);
        }
      }
      // Error handling is now done in the OrderContext via TradingErrorContext
    } catch (error) {
      console.error('Error executing buy order:', error);
    } finally {
      setIsBuyLoading(false);
    }
  }, [currentPrice, symbol, executeOrder, showBuyInput, buyPrice, setBuyPriceInputVisible, setOnRetry, accountBalance, accountType, isBuyLoading]);

  // Handle Sell button click
  const handleSellClick = useCallback(async () => {
    if (isSellLoading) return; // Prevent multiple clicks
    
    setIsSellLoading(true);
    try {
      let orderDetails;
      
      // Calculate trade amount as 20% of total balance
      const currentBalance = accountBalance.totalBalance;
      const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
      const currentPriceNum = parseFloat(currentPrice.replace(/[,$]/g, ''));
      const amount = Math.max(1.10, tradeAmountUSD / currentPriceNum); // Minimum 1.10 quantity
      
      // If input is visible, determine order type based on price comparison
      if (showSellInput && sellPrice) {
        const inputPrice = parseFloat(sellPrice);
        const orderTypeDetection = detectOrderType('sell', inputPrice, currentPriceNum);
        
        orderDetails = {
          symbol: symbol,
          side: 'sell' as const,
          type: orderTypeDetection.orderType as 'limit' | 'stop',
          price: orderTypeDetection.isStopOrder ? currentPriceNum : inputPrice, // For stop orders, use current price as execution price
          stopPrice: orderTypeDetection.isStopOrder ? inputPrice : undefined, // For stop orders, use input price as stop price
          amount: amount,
          leverage: 100, // 100x leverage
        };
      } else {
        // Execute market order
        orderDetails = {
          symbol: symbol,
          side: 'sell' as const,
          type: 'market' as const,
          price: currentPriceNum,
          amount: amount,
          leverage: 100, // 100x leverage
        };
      }

      // Set up retry functionality
      setOnRetry(() => () => handleSellClick());
      
      // Execute the order
      const result = await executeOrder(orderDetails);
      
      if (result.success) {
        // Reset input state after successful order
        if (showSellInput) {
          setSellPriceInputVisible(false);
        }
      }
      // Error handling is now done in the OrderContext via TradingErrorContext
    } catch (error) {
      console.error('Error executing sell order:', error);
    } finally {
      setIsSellLoading(false);
    }
  }, [currentPrice, symbol, executeOrder, showSellInput, sellPrice, setSellPriceInputVisible, setOnRetry, accountBalance, accountType, isSellLoading]);

  // Handle Buy button hold
  const handleBuyHold = useCallback(() => {
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      fillBuyPrice(currentPrice.replace(/[,$]/g, ''));
      setIsHolding(false);
    }, 500); // 500ms hold time
  }, [currentPrice, fillBuyPrice]);

  // Handle Sell button hold
  const handleSellHold = useCallback(() => {
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      fillSellPrice(currentPrice.replace(/[,$]/g, ''));
      setIsHolding(false);
    }, 500); // 500ms hold time
  }, [currentPrice, fillSellPrice]);

  // Handle button release
  const handleButtonRelease = useCallback(() => {
    setIsHolding(false);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  }, []);

  // Close input functions
  const closeBuyInput = useCallback(() => {
    setBuyPriceInputVisible(false);
  }, [setBuyPriceInputVisible]);

  const closeSellInput = useCallback(() => {
    setSellPriceInputVisible(false);
  }, [setSellPriceInputVisible]);

  // Handle drawing tool selection
  const handleDrawingToolSelect = useCallback((tool: DrawingTool) => {
    setSelectedDrawingTool(tool);
  }, []);

  // Handle drawing completion
  const handleDrawingComplete = useCallback((element: DrawingElement) => {
    setDrawingElements(prev => [...prev, element]);
  }, []);

  // Handle clear all drawings
  const handleClearAllDrawings = useCallback(() => {
    setDrawingElements([]);
  }, []);

  // Get current asset configuration from context
  const currentAssetConfig = getStrategy(symbol);

  // AI state is now handled by the unified hook
  
  // For backward compatibility, also check local running state
  const isAIRunning = aiState.isRunning || aiCurrentAssetConfig.isRunning;
  
  // Force re-render when refreshTrigger changes
  useEffect(() => {
    // No logging needed
  }, [refreshTrigger, symbol]);

  // Use unified AI click handler
  const handleAIClick = handlers.handleStartStop;

  // Handle strategy change
  const handleStrategyChange = useCallback(async (strategyId: string) => {
    try {
      // If strategyId is empty, it means we're removing the strategy
      if (!strategyId) {
        await handlers.handleRemoveStrategy();
        return;
      }
      
      // Update legacy strategy for backward compatibility
      updateStrategy(symbol, { strategy: strategyId });
    } catch (error) {
      console.error(`❌ TradingChart: Error handling strategy change for ${symbol}:`, error);
    }
  }, [symbol, updateStrategy, handlers]);

  // Handle config update
  const handleConfigUpdate = useCallback((config: any) => {
    updateConfig(symbol, config);
  }, [symbol, updateConfig]);

  // Handle signal update
  const handleSignalUpdate = useCallback((signal: 'buy' | 'sell' | null) => {
    updateSignal(symbol, signal);
  }, [symbol, updateSignal]);

  // Handle strategy settings click
  const handleStrategySettingsClick = useCallback(async () => {
    if (isSettingsLoading) return; // Prevent multiple clicks
    
    setIsSettingsLoading(true);
    try {
      setShowAISettings(true);
      // Brief delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      setIsSettingsLoading(false);
    }
  }, [isSettingsLoading]);

  // Handle AI Start/Stop with loading state
  const handleAIStartStop = useCallback(async () => {
    if (isAILoading) return; // Prevent multiple clicks
    
    setIsAILoading(true);
    try {
      if (isAIRunning) {
        await handlers.handleStopAI();
      } else {
        await handlers.handleStartAI();
      }
    } catch (error) {
      console.error('Error handling AI start/stop:', error);
    } finally {
      setIsAILoading(false);
    }
  }, [isAILoading, isAIRunning, handlers]);


  // Apply better price scale configuration to prevent current candle from dominating
  const applyPriceScaleConfig = useCallback(() => {
    if (!chartRef.current) return;
    
    try {
      const priceScale = chartRef.current.priceScale('right');
      priceScale.applyOptions({
        autoScale: false,
        scaleMargins: {
          top: 0.2, // Increased top margin to prevent current candle from reaching top
          bottom: 0.2, // Increased bottom margin to prevent current candle from reaching bottom
        },
      });
    } catch (error) {
      console.warn('Error applying price scale config:', error);
    }
  }, []);

  // Reset chart function (like TradingView reset) - but don't auto-scroll to right edge
  const resetChart = useCallback(() => {
    if (chartRef.current && chartData.length > 0) {
      try {
        const timeScale = chartRef.current.timeScale();
        
        // Don't use fitContent() as it automatically scrolls to the right edge
        // Instead, set a reasonable visible range without forcing position
        
        // Set reasonable visible range to prevent excessive automatic scaling
        const lastBarTime = chartData[chartData.length - 1].time;
        // Show a reasonable number of bars based on data length
        let visibleBars;
        if (chartData.length <= 50) {
          visibleBars = chartData.length;
        } else if (chartData.length <= 200) {
          visibleBars = Math.floor(chartData.length * 0.8);
        } else {
          visibleBars = Math.floor(chartData.length * 0.6);
        }
        const firstVisibleTime = chartData[Math.max(0, chartData.length - visibleBars)].time;
        
        // Only set visible range if it's not already set or if user hasn't manually positioned
        const currentRange = timeScale.getVisibleRange();
        if (!currentRange && !hasUserPositionedChart) {
          timeScale.setVisibleRange({
            from: firstVisibleTime as UTCTimestamp,
            to: lastBarTime as UTCTimestamp,
          });
        }
        
        // Reset bar spacing to balanced values
        timeScale.applyOptions({
          barSpacing: 6, // Standard TradingView spacing
          minBarSpacing: 0.5, // More flexible for better interaction
        });
      } catch (error) {
        console.warn('Error resetting chart:', error);
      }
    }
  }, [chartData, hasUserPositionedChart]);

  // Debounced loader state management to prevent flickering and constant reloading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isLoading) {
      // Only show loader if it's not already showing to prevent constant reloading
      if (!showLoader) {
        setShowLoader(true);
      }
    } else {
      // Hide loader with a delay to prevent flickering
      timeoutId = setTimeout(() => {
        setShowLoader(false);
      }, 500); // Increased delay to prevent rapid show/hide cycles
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, showLoader]);

  // Add keyboard shortcut for reset (like TradingView)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Reset chart on Home key or Ctrl+R
      if (event.key === 'Home' || (event.ctrlKey && event.key === 'r')) {
        event.preventDefault();
        resetChart();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [resetChart]);

  // Determine if symbol is crypto
  const isCrypto = symbol.includes('USDT') || symbol.includes('BTC') || symbol.includes('ETH') || 
                   symbol.includes('SOL') || symbol.includes('ADA') || symbol.includes('DOT') || 
                   symbol.includes('LINK') || symbol.includes('DOGE') || symbol.includes('XRP') ||
                   symbol.includes('BCH') || symbol.includes('DASH') || symbol.includes('LTC') ||
                   symbol.includes('XLM') || symbol.includes('XMR');

  // Convert timeframe to Binance interval
  const getBinanceInterval = useCallback((tf: string): string => {
    const normalizedTf = tf.toLowerCase();
    
    switch (normalizedTf) {
      case '1m': return '1m';
      case '5m': return '5m';
      case '15m': return '15m';
      case '30m': return '30m';
      case '1h': return '1h';
      case '4h': return '4h';
      case '6h': return '6h';
      case '12h': return '12h';
      case '1d': return '1d';
      case '1w': return '1w';
      case '1M': return '1M';
      case '2M': return '2M';
      case '3M': return '3M';
      case '6M': return '6M';
      case '1Y': return '1y';
      default: 
        console.warn(`Unknown timeframe: ${tf}, defaulting to 1h`);
        return '1h';
    }
  }, []);

  // Rate limiting state
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Rate limiting constants
  const MIN_FETCH_INTERVAL = 5000; // 5 seconds minimum between requests (increased from 2s)
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 10000; // 10 seconds delay between retries (increased from 5s)

  // Fetch crypto data from Binance with rate limiting
  const fetchCryptoData = useCallback(async (skipChartUpdate = false) => {
    const now = Date.now();
    
    // Prevent concurrent fetch calls
    if (fetchInProgressRef.current) {
      return;
    }
    
    // Rate limiting check
    if (now - lastFetchTime < MIN_FETCH_INTERVAL) {
      return;
    }
    
    // If rate limited, wait before retrying
    if (isRateLimited) {
      return;
    }
    
    // Mark fetch as in progress
    fetchInProgressRef.current = true;
    
    try {
      const binanceInterval = getBinanceInterval(timeframe);
      
      // For crypto symbols, use the symbol directly (already in USDT format)
      let symbolPair = symbol;
      if (isCrypto) {
        symbolPair = symbol;
      }
      
      
      const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbolPair}&interval=${binanceInterval}&limit=500`);
      
      if (!response.ok) {
        if (response.status === 429) {
          // Rate limited by Binance
          setIsRateLimited(true);
          setRetryCount(prev => prev + 1);
          console.warn('Binance API rate limited, will retry later');
          
          // Reset rate limit after longer delay for 429 errors
          setTimeout(() => {
            setIsRateLimited(false);
          }, RETRY_DELAY * 2); // Wait longer for 429 errors
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error(`Invalid response format: expected array, got ${typeof data}`);
      }

      const klines: BinanceKline[] = data;
      
      // Reset retry count on successful fetch
      setRetryCount(0);
      setLastFetchTime(now);
      
      if (klines.length > 0) {
        const latestKline = klines[klines.length - 1];
        const previousKline = klines[klines.length - 2];
        
        // Extract candlestick data
        const openPrice = parseFloat(latestKline[1]);
        const highPrice = parseFloat(latestKline[2]);
        const lowPrice = parseFloat(latestKline[3]);
        const closePrice = parseFloat(latestKline[4]);
        const volume = parseFloat(latestKline[5]);
        
        // Calculate price change and percentage
        if (previousKline) {
          const previousClose = parseFloat(previousKline[4]);
          const change = closePrice - previousClose;
          const changePercent = (change / previousClose) * 100;
          
          const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
          setCurrentPrice(formattedPrice);
          setPriceChange(`${change >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`);
          setIsPositiveChange(change >= 0);
          setLastUpdateTime(new Date().toLocaleTimeString());

          // Update AI trading service with current price
          updatePriceForAI(symbol, formattedPrice);

          onPriceUpdate?.(formattedPrice, `${change >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`, change >= 0);
        }
        
        // Call the kline price update callback
        const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
        onKlinePriceUpdate?.(formattedPrice, closePrice, volume);
        
        // Process chart data
        const processedChartData: CandlestickData[] = klines.map((kline) => ({
          time: (kline[0] / 1000) as UTCTimestamp,
          open: parseFloat(kline[1]),
          high: parseFloat(kline[2]),
          low: parseFloat(kline[3]),
          close: parseFloat(kline[4]),
        }));
        
        // Process volume data with color information
        const greenVolumeData: LineData[] = [];
        const redVolumeData: LineData[] = [];
        
        klines.forEach((kline) => {
          const open = parseFloat(kline[1]);
          const close = parseFloat(kline[4]);
          const volume = parseFloat(kline[5]);
          const isGreen = close >= open;
          
          const volumePoint = {
            time: (kline[0] / 1000) as UTCTimestamp,
            value: volume,
          };
          
          if (isGreen) {
            greenVolumeData.push(volumePoint);
          } else {
            redVolumeData.push(volumePoint);
          }
        });
        
        setChartData(processedChartData);
        
        // Apply better price scale configuration to prevent current candle from appearing at full height
        applyPriceScaleConfig();
        
        // Store separate volume data for green and red series
        setGreenVolumeData(greenVolumeData);
        setRedVolumeData(redVolumeData);
        
        // Update chart if it exists - only update new data to preserve chart position
        if (candlestickSeriesRef.current && !skipChartUpdate) {
          try {
            
            // Get the last existing data point to avoid resetting chart position
            const lastExistingTime = chartData.length > 0 ? chartData[chartData.length - 1].time : 0;
            
            // Only add new data points, don't replace entire dataset
            const newChartData = processedChartData.filter(item => {
              // Ensure time is a number for comparison
              const itemTime = typeof item.time === 'number' ? item.time : Number(item.time);
              const lastTime = typeof lastExistingTime === 'number' ? lastExistingTime : Number(lastExistingTime);
              return itemTime > lastTime;
            });
            
            if (newChartData.length > 0) {
              // Convert new data to appropriate format for different chart types
              let newSeriesData: any[] = [];
              
              switch (chartType) {
                case 'line':
                case 'area':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'bar':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'histogram':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'candlestick':
                default:
                  newSeriesData = newChartData;
                  break;
              }
              
              // Only update the last data point to minimize chart disruption
              const lastDataPoint = newSeriesData[newSeriesData.length - 1];
              if (lastDataPoint) {
                candlestickSeriesRef.current.update(lastDataPoint);
              }
              
              // Update volume data only for candlestick charts
              if (chartType === 'candlestick' && SHOW_VOLUME_BARS && greenVolumeSeriesRef.current && redVolumeSeriesRef.current) {
                const newGreenVolumeData = greenVolumeData.filter(item => item.time > lastExistingTime);
                const newRedVolumeData = redVolumeData.filter(item => item.time > lastExistingTime);
                
                if (newGreenVolumeData.length > 0) {
                  greenVolumeSeriesRef.current.update(newGreenVolumeData[newGreenVolumeData.length - 1]);
                }
                if (newRedVolumeData.length > 0) {
                  redVolumeSeriesRef.current.update(newRedVolumeData[newRedVolumeData.length - 1]);
                }
              }
            }

          } catch (error) {
            console.warn('Error updating chart data:', error);
          }
        }
      }
      
      // Always update loading state when data is successfully fetched
      setIsLoading(false);
      setIsSymbolChanging(false);
      
      // Clear the loading timeout since we successfully fetched data
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clear fetch in progress flag
      fetchInProgressRef.current = false;
      
      return data;
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      
      // Handle different types of errors
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        console.warn('Network error or rate limiting detected');
        setIsRateLimited(true);
        
        // Only retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            setIsRateLimited(false);
          }, RETRY_DELAY * (retryCount + 1)); // Exponential backoff
        } else {
          console.error('Max retries exceeded, giving up');
          // Reset retry count after max retries to allow future attempts
          setTimeout(() => {
            setRetryCount(0);
            setIsRateLimited(false);
          }, RETRY_DELAY * 2); // Wait longer before allowing new attempts
        }
      }
      
      // Always update loading state even on error to prevent stuck loading state
      setIsLoading(false);
      setIsSymbolChanging(false);
      
      // Clear the loading timeout even on error
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clear fetch in progress flag
      fetchInProgressRef.current = false;
      
      return [];
    }
  }, [symbol, timeframe, isCrypto, onPriceUpdate, onKlinePriceUpdate, getBinanceInterval, applyPriceScaleConfig]);

  // Initialize lightweight-charts
  const initializeChart = useCallback(() => {
    if (!chartContainerRef.current) return;

    // Clean up existing chart safely
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (error) {
        console.warn('Error removing existing chart:', error);
      }
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      greenVolumeSeriesRef.current = null;
      redVolumeSeriesRef.current = null;
    }

    // Clear container
    chartContainerRef.current.innerHTML = '';

    // Create new chart with theme-aware colors
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { color: theme === 'dark' ? '#1e293b' : '#ffffff' },
        textColor: theme === 'dark' ? '#88909f' : '#6b7280', // More muted text color
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide vertical grid lines
        horzLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide horizontal grid lines
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
        horzLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
      },
      rightPriceScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        scaleMargins: {
          top: 0.2, // Increased margins to prevent current candle from reaching chart edges
          bottom: 0.2,
        },
        autoScale: false, // Disable auto-scaling to prevent current candle from appearing at full height
        alignLabels: true,
        entireTextOnly: false,
        visible: true,
        ticksVisible: true,
        borderVisible: true,
      },
      timeScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5, // Match TradingView default - no right offset
        barSpacing: 6, // Standard TradingView spacing
        minBarSpacing: 0.1, // More flexible for better interaction

        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: false, // Disable to allow free dragging without snapping back
        shiftVisibleRangeOnNewBar: false, // Keep disabled to prevent focus jumping
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true, // Re-enable axis scaling for user interaction
        mouseWheel: true, // Re-enable mouse wheel zoom for user interaction
        pinch: true, // Re-enable pinch zoom for user interaction
      },
    });

    // Create chart series based on chartType
    let mainSeries: any = null;
    
    switch (chartType) {
      case 'line':
        mainSeries = chart.addSeries(LineSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'area':
        mainSeries = chart.addSeries(AreaSeries, {
          lineColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          topColor: theme === 'dark' ? '#059669' : '#10b981',
          bottomColor: theme === 'dark' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(16, 185, 129, 0.1)', // More muted area fill
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'bar':
        mainSeries = chart.addSeries(HistogramSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'histogram':
        mainSeries = chart.addSeries(HistogramSeries, {
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: false,
          priceLineVisible: false,
          priceFormat: {
            type: 'volume',
          },
        });
        break;
        
      case 'candlestick':
      default:
        mainSeries = chart.addSeries(CandlestickSeries, {
          upColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          downColor: theme === 'dark' ? '#dc2626' : '#ef4444', // More muted red
          borderDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          borderUpColor: theme === 'dark' ? '#059669' : '#10b981',
          wickDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          wickUpColor: theme === 'dark' ? '#059669' : '#10b981',
          // Enhanced styling for better visual appearance
          borderVisible: true,
          wickVisible: true,
          // Price formatting
          priceFormat: {
            type: 'price',
            precision: 2,
            minMove: 0.01,
          },
        });
        break;
    }

    // Create separate volume series for green and red volumes (only for candlestick)
    let greenVolumeSeries: any = null;
    let redVolumeSeries: any = null;
    
    if (chartType === 'candlestick' && SHOW_VOLUME_BARS) {
      greenVolumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(38, 166, 154, 0.5)', // Green volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(240, 69, 66, 0.4)', // Red volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });

      // Set volume series position and styling with theme-aware colors
      greenVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });
      
      redVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });

      // Apply histogram-style thin bar styling to volume series
      greenVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
    }

    // Store references
    chartRef.current = chart;
    candlestickSeriesRef.current = mainSeries;
    setMainSeries(mainSeries); // Set the main series for position overlay
    greenVolumeSeriesRef.current = greenVolumeSeries;
    redVolumeSeriesRef.current = redVolumeSeries;

    // Set initial data if available
    if (chartData.length > 0) {
      try {
        // Convert candlestick data to appropriate format for different chart types
        let seriesData: any[] = [];
        
        switch (chartType) {
          case 'line':
          case 'area':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'bar':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'histogram':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'candlestick':
          default:
            seriesData = chartData;
            break;
        }
        
        mainSeries.setData(seriesData);
        
        // Set volume data only for candlestick charts
        if (chartType === 'candlestick' && SHOW_VOLUME_BARS && greenVolumeSeries && redVolumeSeries) {
          greenVolumeSeries.setData(greenVolumeData);
          redVolumeSeries.setData(redVolumeData);
        }

        // Don't auto-fit the chart to prevent automatic scrolling to right edge
        // Let the user position the chart as they want
        // setTimeout(() => {
        //   resetChart();
        // }, 100);
      } catch (error) {
        console.warn('Error setting initial chart data:', error);
      }
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        try {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
        } catch (error) {
          console.warn('Error resizing chart:', error);
        }
      }
    };

    // Handle user interaction events
    const handleUserInteractionStart = () => {
      setIsUserInteracting(true);
    };

    const handleUserInteractionEnd = () => {
      // Mark that user has positioned the chart
      setHasUserPositionedChart(true);
      
      // Add a longer delay before re-enabling updates to allow smooth positioning
      setTimeout(() => {
        setIsUserInteracting(false);
      }, 1000);
    };

    // Add event listeners for user interaction (with passive option for scroll-blocking events)
    if (chartContainerRef.current) {
      chartContainerRef.current.addEventListener('mousedown', handleUserInteractionStart);
      chartContainerRef.current.addEventListener('wheel', handleUserInteractionStart, { passive: true });
      chartContainerRef.current.addEventListener('touchstart', handleUserInteractionStart, { passive: true });
      
      window.addEventListener('mouseup', handleUserInteractionEnd);
      window.addEventListener('touchend', handleUserInteractionEnd);
    }

    window.addEventListener('resize', handleResize);

    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mouseup', handleUserInteractionEnd);
      window.removeEventListener('touchend', handleUserInteractionEnd);
      
      if (chartContainerRef.current) {
        chartContainerRef.current.removeEventListener('mousedown', handleUserInteractionStart);
        chartContainerRef.current.removeEventListener('wheel', handleUserInteractionStart);
        chartContainerRef.current.removeEventListener('touchstart', handleUserInteractionStart);
      }
    };
  }, [chartData, volumeData, chartType]);

  // Initialize chart when component mounts
  useEffect(() => {
    const cleanup = initializeChart();
    return cleanup;
  }, [initializeChart]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up chart instance
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (error) {
          console.warn('Error removing chart on unmount:', error);
        }
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        greenVolumeSeriesRef.current = null;
        redVolumeSeriesRef.current = null;
      }
      
      // Clean up loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Clean up hold timeout
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
        holdTimeoutRef.current = null;
      }
    };
  }, []);

  // Reinitialize chart when symbol, timeframe, or theme changes
  useEffect(() => {
    // Small delay to ensure previous chart is fully cleaned up
    const timer = setTimeout(() => {
      initializeChart();
      }, 100);

    return () => clearTimeout(timer);
  }, [symbol, timeframe, theme, initializeChart]);

  // Reinitialize chart when chartType or theme changes
  useEffect(() => {
    initializeChart();
  }, [chartType, theme, initializeChart]);

  // Detect symbol changes and set loading state
  useEffect(() => {
    if (prevSymbol !== symbol && symbol) {
      // Always set loading state when symbol changes, regardless of current loading state
      setIsSymbolChanging(true);
      setIsLoading(true);
      setPrevSymbol(symbol);
      
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Set a timeout to prevent loading state from getting stuck indefinitely
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn(`Loading timeout for symbol ${symbol}, forcing loading state to false`);
        setIsLoading(false);
        setIsSymbolChanging(false);
        loadingTimeoutRef.current = null;
      }, 10000); // 10 second timeout
      
      // Clean up timeout when component unmounts or symbol changes again
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      };
    }
  }, [symbol, prevSymbol]);

  // Fetch data when component mounts or symbol changes
  useEffect(() => {
    if (isCrypto && !isRateLimited) {
      fetchCryptoData();
    } else if (!isCrypto) {
      setIsLoading(false);
      setIsSymbolChanging(false);
    }
  }, [symbol, isCrypto, fetchCryptoData, isRateLimited]);

  // Real-time listener for AI trading state changes
  useEffect(() => {
    if (!user) return;

    const userDocRef = doc(db, 'userDocuments', user.uid);
    const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
    
    
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        const accountData = userData.accounts?.[firestoreAccountType];
        
        if (accountData?.aiTradingData) {
          const aiTradingData = accountData.aiTradingData;
          
          // The BackendAITradingContext will handle the state updates
          // This listener ensures the UI reflects changes immediately
        }
      }
    }, (error) => {
      console.error('❌ Real-time AI trading listener error:', error);
    });

    return () => {
      unsubscribe();
    };
  }, [user, accountType]);

  // Real-time updates - much more conservative to prevent excessive API calls
  useEffect(() => {
    if (!isCrypto || isRateLimited) return;

    // Only fetch real-time updates every 30 seconds to prevent rate limiting
    const intervalId = setInterval(() => {
      if (!isRateLimited && isCrypto) {
        fetchCryptoData(true); // Skip chart update to avoid flickering
      }
    }, 30000); // 30 seconds interval

    return () => clearInterval(intervalId);
  }, [isCrypto, isRateLimited, fetchCryptoData]);

  // Conservative real-time tick updates to prevent flickering and position changes
  const updateLastCandleTick = useCallback((newPrice: number) => {
    // Don't update chart if user is interacting to prevent freezing/flickering
    if (isUserInteracting || !candlestickSeriesRef.current) return;

    try {
      // Get the current chart data without depending on state
      setChartData(prevData => {
        if (!prevData.length) return prevData;
        
        // Get the last candle data
        const lastCandle = prevData[prevData.length - 1];
        
        // Validate that we have a valid timestamp
        if (!lastCandle.time || typeof lastCandle.time !== 'number') {
          console.warn('Invalid timestamp in last candle:', lastCandle);
          return prevData;
        }
        
        // Create updated candle with new close price
        const updatedCandle = {
          time: lastCandle.time,
          open: lastCandle.open,
          high: Math.max(lastCandle.high, newPrice),
          low: Math.min(lastCandle.low, newPrice),
          close: newPrice,
        };

        // Update only the last candle without triggering chart reload or position changes
        if (candlestickSeriesRef.current) {
          try {
            // Convert data format based on chart type
            let updateData: any;
            
            switch (chartType) {
              case 'line':
              case 'area':
              case 'bar':
              case 'histogram':
                // For non-candlestick charts, use { time, value } format
                updateData = {
                  time: updatedCandle.time,
                  value: updatedCandle.close,
                };
                break;
              case 'candlestick':
              default:
                // For candlestick charts, use full candlestick data
                updateData = updatedCandle;
                break;
            }
            
            candlestickSeriesRef.current.update(updateData);
            
            // Note: We removed the dynamic price scale range update to avoid API issues
            // The initial price scale range set during data loading should be sufficient
            // for preventing the current candle from appearing at full height
          } catch (updateError) {
            // Suppress the specific "Cannot update oldest data" error
            if (updateError instanceof Error && updateError.message && updateError.message.includes('Cannot update oldest data')) {
              console.warn('Chart update skipped - data timestamp conflict:', updateError.message);
              return prevData; // Return unchanged data
            }
            throw updateError; // Re-throw other errors
          }
        }
        
        // Return updated data
        const newData = [...prevData];
        newData[newData.length - 1] = updatedCandle;
        return newData;
      });
    } catch (error) {
      // Suppress logging for specific chart update errors
      if (error instanceof Error && error.message && error.message.includes('Cannot update oldest data')) {
        return; // Silently ignore this specific error
      }
      console.warn('Error updating last candle tick:', error);
    }
  }, [isUserInteracting, chartType]);

  // Real-time price updates from Binance WebSocket service - Optimized for performance
  useEffect(() => {
    if (!isCrypto || !isBinanceConnected) return;

    let lastUpdateTime = 0;
    const UPDATE_THROTTLE_MS = 500; // Throttle updates to every 500ms for better performance

    const updatePriceFromBinance = () => {
      const now = Date.now();
      
      // Throttle updates to prevent excessive re-renders
      if (now - lastUpdateTime < UPDATE_THROTTLE_MS) {
        return;
      }
      lastUpdateTime = now;

      const marketData = getMarketDataForSymbol(symbol);
      if (marketData) {
        const { price, change, isPositive } = marketData;
        
        // Extract numeric price for chart update
        const numericPrice = parseFloat(price.replace(/[,$]/g, ''));
        
        // Batch state updates to prevent multiple re-renders
        requestAnimationFrame(() => {
          setCurrentPrice(price);
          setPriceChange(change);
          setIsPositiveChange(isPositive);
          setLastUpdateTime(new Date().toLocaleTimeString());
          
          // Update chart tick smoothly without reload
          updateLastCandleTick(numericPrice);
          
          // Update AI trading service with current price
          updatePriceForAI(symbol, price);
          
          onPriceUpdate?.(price, change, isPositive);
        });
      }
    };

    updatePriceFromBinance();
    
    // Reduced update frequency for better performance
    const interval = setInterval(updatePriceFromBinance, 1000);
    
    return () => clearInterval(interval);
  }, [isCrypto, isBinanceConnected, symbol, getMarketDataForSymbol, onPriceUpdate, updateLastCandleTick]);

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        try {
          setIsChartDisposed(true);
          chartRef.current.remove();
        } catch (error) {
          console.warn('Error removing chart:', error);
        }
      }
    };
  }, []);

  return (
    <div 
      className={`relative w-full h-full ${theme === 'light' ? 'bg-white border border-0 border-gray-100' : 'border-gray-700'}`}
    >
      {/* Always render the loader but control visibility with CSS */}
      <div 
        className={`absolute inset-0 flex items-center justify-center theme-bg-primary rounded-lg text-xs transition-opacity duration-500 z-10 ${showLoader ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {showLoader && (
          <Loader 
            size="md" 
            title="" 
            subtitle={isSymbolChanging ? `Loading ${symbol} chart data...` : "Loading chart data..."} 
          />
        )}
      </div>
      
      {/* Chart content - always rendered but hidden when loading */}
      <div className={`w-full h-full transition-opacity duration-500 ${showLoader ? 'opacity-0' : 'opacity-100'}`}>
        {/* Chart Container */}
        <div 
          ref={chartContainerRef} 
          className="w-full h-full candlestick-chart"
          style={{
            // Add CSS for rounded corners on candlesticks
            '--candlestick-border-radius': '2px',
            // Ensure proper interaction
            pointerEvents: 'auto',
            touchAction: 'pan-x pan-y',
          } as React.CSSProperties}
        />
        
        {/* Position Manager */}
        {mainSeries && !isChartDisposed && (
          <ChartPositionManager
            chartRef={chartRef}
            seriesRef={mainSeries}
            symbol={symbol}
            currentPrice={parseFloat(currentPrice.replace(/[,$]/g, ''))}
            isVisible={showPositionOverlay && !showLoader}
          />
        )}
      </div>
      
      {/* Rate Limited Indicator */}
      {isRateLimited && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="text-center text-white">
            <div className="text-sm font-medium mb-2">Rate Limited</div>
            <div className="text-xs opacity-75">Too many requests, retrying in a moment...</div>
            {retryCount > 0 && (
              <div className="text-xs opacity-50 mt-1">Attempt {retryCount}/{MAX_RETRIES}</div>
            )}
          </div>
        </div>
      )}
      
      {/* Price overlay */}
      <div className="absolute bottom-8 left-1 theme-bg-secondary bg-opacity-95 p-3 py-2 rounded-lg z-10 shadow-xs border theme-border">
        {showDateTime && lastUpdateTime && (
          <div className="text-xs theme-text-quaternary">
            {(() => {
              // Format today's date as "Today, 30th August, 2025"
              const now = new Date();
              const day = now.getDate();
              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];
              // Get ordinal suffix for the day
              function getOrdinal(n: number) {
                if (n > 3 && n < 21) return n + "th";
                switch (n % 10) {
                  case 1: return n + "st";
                  case 2: return n + "nd";
                  case 3: return n + "rd";
                  default: return n + "th";
                }
              }
              const formattedDate = `Today, ${getOrdinal(day)} ${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
              // Format time as "8:17:14 AM"
              const formattedTime = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
              return (
                <>                
                  {formattedDate} at {lastUpdateTime}
                </>
              );
            })()}
            {/* {new Date().toLocaleDateString()} {lastUpdateTime} */}
          </div>
        )}
      </div>
      
      {/* Trading Buttons */}
      <div className="absolute top-2 left-2 z-20 flex gap-2">
        {/* AI Button Group */}
        <div className="flex">
          {/* AI Button */}
          
          <button
            data-ai-button
            onClick={handleAIStartStop}
            disabled={isAILoading}
            className={`
              px-2 py-1 text-xs font-medium transition-colors duration-200 flex items-center gap-1
              ${(aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) 
                ? 'rounded-l' 
                : 'rounded'
              }
              ${isAILoading 
                ? 'opacity-50 cursor-not-allowed' 
                : isAIRunning 
                  ? (theme === 'dark' 
                      ? 'bg-green-500/30 text-green-300 hover:bg-green-500/40' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200')
                  : (aiState.canStart || (aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled)
                    ? (theme === 'dark' 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100')
                    : (theme === 'dark' 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100')
              }
            `}
            title={
              isAILoading 
                ? (isAIRunning ? 'Stopping AI...' : 'Starting AI...')
                : isAIRunning 
                  ? 'Stop AI Trading' 
                  : ((aiState.canStart || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null))
                      ? 'Start AI Trading' 
                      : 'Select AI Strategy')
            }
          >
            {isAILoading ? (
              <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) ? (
              isAIRunning ? (
                <Pause className="h-3 w-3" />
              ) : (
                <Play className="h-3 w-3" />
              )
            ) : null}
            AI
          </button>

          {/* AI Settings Button - Only show when strategy is selected */}
          {(aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) && (
            <button
              onClick={handleStrategySettingsClick}
              disabled={isSettingsLoading}
              className={`
                p-1 rounded-r transition-colors duration-200 flex items-center justify-center
                ${isSettingsLoading 
                  ? 'opacity-50 cursor-not-allowed' 
                  : theme === 'dark' 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-green-50 text-green-600 hover:bg-green-100'
                }
              `}
              title="Strategy Settings"
            >
              {isSettingsLoading ? (
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Settings className="h-3 w-3" />
              )}
            </button>
          )}
        </div>

        {/* AI Strategy Display */}
        {(aiState.canStart || aiState.isRunning || (aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled) && (
          <div className={`
            px-2 py-1 text-xs font-medium rounded transition-colors duration-200
            ${theme === 'dark' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-green-50 text-green-600 border border-green-200'
            }
          `}>
            {(aiCurrentFirestoreStrategy as any)?.aiSelectedStrategy || 
             (aiCurrentAssetConfig.strategy === 'stochastic' ? 'MiAI' :
              aiCurrentAssetConfig.strategy === 'supertrend' ? 'LazyB CrazyB' : 
              aiCurrentAssetConfig.strategy === 'bollingerbands' ? 'Huge Hug' :
              aiCurrentAssetConfig.strategy === 'rsi' ? 'AI Assistant' : 
              (aiCurrentFirestoreStrategy as any)?.name || 'LazyB CrazyB')}
            {isAIRunning && (
              <span className="ml-1 text-green-400">●</span>
            )}
          </div>
        )}
        
        {/* Buy Button with Hold Functionality */}
        <div className="flex items-center space-x-1">
          <button
            onClick={handleBuyClick}
            onMouseDown={handleBuyHold}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={handleBuyHold}
            onTouchEnd={handleButtonRelease}
            disabled={isBuyLoading}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1
              ${isHolding 
                ? 'scale-95' 
                : ''
              }
              ${isBuyLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : theme === 'dark' 
                  ? 'bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30' 
                  : 'bg-[#2AB7A6]/10 text-[#2AB7A6] hover:bg-[#2AB7A6]/20'
              }
            `}
            title={showBuyInput ? (buyOrderType === 'stop' ? "Buy Stop Order" : "Buy Limit Order") : "Buy Market Order (Hold for Limit Order)"}
          >
            {isBuyLoading ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                <span>Buying...</span>
              </>
            ) : (
              'Buy'
            )}
          </button>
          
          {/* Buy Price Input - appears when hold is triggered */}
          {showBuyInput && (
            <>
              <NumberInput
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                className="w-20"
                size="sm"
                placeholder="Price"
                step="0.00001"
                autoFocus
              />
              {/* Order Type Indicator */}
              {buyOrderType && (
                <div className={`
                  px-2 py-1 text-xs font-medium rounded
                  ${buyOrderType === 'stop' 
                    ? theme === 'dark' 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-orange-100 text-orange-600'
                    : theme === 'dark'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                  }
                `}>
                  {buyOrderType === 'stop' ? 'STOP' : 'LIMIT'}
                </div>
              )}
              <button
                onClick={closeBuyInput}
                className={`
                  p-1 rounded transition-colors duration-200
                  ${theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
                title="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
        
        {/* Sell Button with Hold Functionality */}
        <div className="flex items-center space-x-1">
          <button
            onClick={handleSellClick}
            onMouseDown={handleSellHold}
            onMouseUp={handleButtonRelease}
            onMouseLeave={handleButtonRelease}
            onTouchStart={handleSellHold}
            onTouchEnd={handleButtonRelease}
            disabled={isSellLoading}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center space-x-1
              ${isHolding 
                ? 'scale-95' 
                : ''
              }
              ${isSellLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : theme === 'dark' 
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
              }
            `}
            title={showSellInput ? (sellOrderType === 'stop' ? "Sell Stop Order" : "Sell Limit Order") : "Sell Market Order (Hold for Limit Order)"}
          >
            {isSellLoading ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                <span>Selling...</span>
              </>
            ) : (
              'Sell'
            )}
          </button>
          
          {/* Sell Price Input - appears when hold is triggered */}
          {showSellInput && (
            <>
              <NumberInput
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                className="w-20"
                size="sm"
                placeholder="Price"
                step="0.00001"
                autoFocus
              />
              {/* Order Type Indicator */}
              {sellOrderType && (
                <div className={`
                  px-2 py-1 text-xs font-medium rounded
                  ${sellOrderType === 'stop' 
                    ? theme === 'dark' 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-orange-100 text-orange-600'
                    : theme === 'dark'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                  }
                `}>
                  {sellOrderType === 'stop' ? 'STOP' : 'LIMIT'}
                </div>
              )}
              <button
                onClick={closeSellInput}
                className={`
                  p-1 rounded transition-colors duration-200
                  ${theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
                title="Close"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
        
        {/* Position Overlay Toggle Button - Only show when there are positions */}
        {chartPositions.filter(pos => pos.symbol === symbol && pos.isVisible !== false).length > 0 && (
          <button
            onClick={() => setShowPositionOverlay(!showPositionOverlay)}
            className={`
              px-2 py-1 text-xs font-medium rounded transition-colors duration-200 flex items-center justify-center
              ${theme === 'dark' 
                ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
            title={showPositionOverlay ? "Hide Position Overlay" : "Show Position Overlay"}
          >
            {showPositionOverlay ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
      
      {/* Charting Tools - Left Side 
      <ChartingTools
        onToolSelect={handleDrawingToolSelect}
        selectedTool={selectedDrawingTool}
        onDrawingComplete={handleDrawingComplete}
        onClearAll={handleClearAllDrawings}
        isVisible={!showLoader}
        chartContainerRef={chartContainerRef}
      />
      */}
      {/* AI Trading Settings Popup */}
      <AITradingSettings
        isOpen={showAISettings}
        onClose={() => setShowAISettings(false)}
        symbol={symbol}
        currentPrice={currentPrice}
        selectedStrategy={
          // Use new per-asset system first, fallback to legacy system (same as AITradePanel)
          assetAIConfig?.activeStrategyInstance?.type ||
          assetAIConfig?.activeStrategyInstance?.id ||
          (aiCurrentFirestoreStrategy as any)?.type ||
          (aiCurrentFirestoreStrategy as any)?.id ||
          aiCurrentAssetConfig.strategy ||
          ''
        }
        onStrategyChange={handleStrategyChange}
        onConfigUpdate={handleConfigUpdate}
        onSignalUpdate={handleSignalUpdate}
        isRunning={aiState.isRunning || aiCurrentAssetConfig.isRunning}
      />

      {/* Trading Error Modal */}
      <TradingErrorModal
        isOpen={!!tradingError}
        onClose={hideError}
        onRetry={retryOrder}
        error={tradingError || { type: 'unknown', message: '' }}
        orderDetails={tradingError?.orderDetails}
      />

      
      {/* Centered Watermark Logo - Theme Aware */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-60" style={{ top: '-130px' }}>
        <div className={`transform scale-100 animate-spin-slow ${theme === 'dark' ? 'opacity-20' : 'opacity-10'}`}>
          {/* Theme-aware logo - black for dark theme, white for light theme */}
          {theme === 'dark' ? (
            <img 
              src="/images/paul_d_black.svg"
              alt="Live Trading by Paul D" 
              className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[350px] h-auto filter drop-shadow-2xl"
            />
          ) : (
            <img 
              src="/images/paul_d_white.svg"
              alt="Live Trading by Paul D" 
              className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[350px] h-auto filter drop-shadow-2xl"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingChart;
